import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { FilterChip } from '@/components/shared/filter-chip';

export interface MultiSelectFilterValue {
  include: string[];
  exclude: string[];
}

export interface MultiSelectFilterProps {
  /** Aktueller Zustand: aufgenommene (`include`) und ausgeschlossene (`exclude`) Werte. */
  value: MultiSelectFilterValue;
  /** Liefert den naechsten Zustand. Ein Wert ist nie gleichzeitig include UND exclude. */
  onChange: (next: MultiSelectFilterValue) => void;
  /** Auswahlquelle (durchsuchbare Combobox). */
  options: ComboboxOption[];
  /** Freitext erlauben (Wert ausserhalb der Options aufnehmen). */
  allowFreeText?: boolean;
  /** Combobox-Platzhalter/Texte (vom Aufrufer i18n-aufgeloest; Defaults aus `common`). */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /**
   * Loest einen rohen Wert in ein Chip-Label auf (z. B. serviceId → DisplayName). Fallback:
   * Option-Label, dann der rohe Wert.
   */
  labelResolver?: (value: string) => string;
  disabled?: boolean;
  id?: string;
  /** Maximale Chip-Label-Breite (CSS-Wert). */
  chipMaxLabelWidth?: string;
}

/**
 * Geteilter Mehrfach/Ausschluss-Filter (Spec 409, Foundation).
 *
 * Eine Combobox dient als „Hinzufuegen"-Picker; gewaehlte Werte erscheinen als
 * {@link FilterChip} mit „ist/ist nicht"-Toggle (+/−). Oeffentlicher Zustand:
 * `{ include, exclude }`. Derselbe Wert landet nie in beiden Listen — der Toggle
 * verschiebt ihn. Rein zustandslos-praesentational (keine URL-/Storage-Kopplung):
 * die Persistenz besorgt der konsumierende Hook. So bleibt der Baustein generisch
 * wiederverwendbar (Transactions, Subscriptions …).
 */
export function MultiSelectFilter({
  value,
  onChange,
  options,
  allowFreeText = false,
  placeholder,
  searchPlaceholder,
  emptyText,
  labelResolver,
  disabled = false,
  id,
  chipMaxLabelWidth,
}: MultiSelectFilterProps) {
  const { t } = useTranslation('common');

  const optionLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of options) map.set(opt.value, opt.label);
    return map;
  }, [options]);

  const resolveLabel = (raw: string): string =>
    labelResolver?.(raw) ?? optionLabel.get(raw) ?? raw;

  // Pick aus der Combobox → in include aufnehmen (aus exclude entfernen), dedupliziert.
  const handlePick = (raw: string) => {
    const picked = raw.trim();
    if (!picked) return;
    onChange({
      include: value.include.includes(picked) ? value.include : [...value.include, picked],
      exclude: value.exclude.filter((v) => v !== picked),
    });
  };

  const toggleMode = (raw: string) => {
    if (value.include.includes(raw)) {
      onChange({
        include: value.include.filter((v) => v !== raw),
        exclude: value.exclude.includes(raw) ? value.exclude : [...value.exclude, raw],
      });
    } else {
      onChange({
        include: value.include.includes(raw) ? value.include : [...value.include, raw],
        exclude: value.exclude.filter((v) => v !== raw),
      });
    }
  };

  const removeValue = (raw: string) => {
    onChange({
      include: value.include.filter((v) => v !== raw),
      exclude: value.exclude.filter((v) => v !== raw),
    });
  };

  const hasChips = value.include.length > 0 || value.exclude.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        // Reiner „Hinzufuegen"-Picker: value bleibt leer, jede Auswahl wird zum Chip.
        value=""
        onChange={handlePick}
        options={options}
        allowFreeText={allowFreeText}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyText={emptyText}
        disabled={disabled}
        id={id}
      />

      {hasChips && (
        // Spec 514: coarse:gap-3 entzerrt die Chip-Trefferflaechen auf Touch.
        <div className="flex flex-wrap items-center gap-2 coarse:gap-3">
          {value.include.map((raw) => (
            <FilterChip
              key={`inc-${raw}`}
              label={resolveLabel(raw)}
              mode="include"
              modeLabel={t('filter.modeInclude')}
              toggleLabel={t('filter.toggleToExclude')}
              onToggleMode={() => toggleMode(raw)}
              onRemove={() => removeValue(raw)}
              removeLabel={t('filter.removeFilter')}
              {...(chipMaxLabelWidth ? { maxLabelWidth: chipMaxLabelWidth } : {})}
            />
          ))}
          {value.exclude.map((raw) => (
            <FilterChip
              key={`exc-${raw}`}
              label={resolveLabel(raw)}
              mode="exclude"
              modeLabel={t('filter.modeExclude')}
              toggleLabel={t('filter.toggleToInclude')}
              onToggleMode={() => toggleMode(raw)}
              onRemove={() => removeValue(raw)}
              removeLabel={t('filter.removeFilter')}
              {...(chipMaxLabelWidth ? { maxLabelWidth: chipMaxLabelWidth } : {})}
            />
          ))}
        </div>
      )}
    </div>
  );
}
